import React, { useEffect, useState, useCallback } from 'react';
import { History, MapPin, Clock, ChevronRight, Loader2, Database, Download } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import ShiftActivityPanel from './ShiftActivityPanel';

// Permanent per-worker movement log. Every tracked shift (stored GPS trail) is listed; open one
// to see the road-following route + visits/time-at-location. Data lives in staff_location_breadcrumbs.
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return d; } };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
const fmtDur = (m) => m == null ? '—' : (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
// Local calendar date — toISOString() is the UTC date, which before 10 am in Australia is yesterday.
const ago = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
// "17:16" (clock times are stored as org-local wall clock) → "5:16 pm", with no Date conversion.
const fmt12 = (hm) => {
  if (!hm) return '—';
  const [h, m] = hm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
};

export default function WorkerActivityLog() {
  const [staff, setStaff] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [from, setFrom] = useState(ago(30));
  const [to, setTo] = useState(ago(0));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null); // { id, name }

  useEffect(() => {
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : (r.data?.staff || []))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!staffId) { setRows([]); return; }
    setLoading(true);
    api.get(`/location/worker/${staffId}/activity`, { params: { from, to } })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [staffId, from, to]);
  useEffect(() => { load(); }, [load]);

  const worker = staff.find(s => s.id === staffId);
  const workerName = worker ? (worker.name || `${worker.first_name || ''} ${worker.last_name || ''}`.trim()) : '';

  const exportCsv = () => {
    if (!rows.length) return toast.error('Nothing to export');
    const head = ['Date', 'Worker', 'Client', 'Start', 'End', 'Tracked (min)', 'At client (min)', 'Community (min)', 'Distance (km)', 'Stops', 'GPS points'];
    const lines = rows.map(r => [
      r.shift_date ? new Date(r.shift_date).toLocaleDateString('en-AU') : (r.started ? new Date(r.started).toLocaleDateString('en-AU') : ''),
      workerName, r.client_name || '', fmtTime(r.started), fmtTime(r.ended),
      r.minutes ?? '', r.client_minutes ?? '', r.community_minutes ?? '', r.raw_km ?? '', r.stops ?? '', r.points ?? '',
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `activity-${workerName.replace(/\s+/g, '_')}-${from}_to_${to}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><History size={20} className="text-primary-600" /> Worker Activity Log</h1>
        <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-0.5"><Database size={13} /> Every tracked shift is saved permanently — open one to see the road route &amp; time at each location.</p>
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-500 mb-1">Support worker</label>
          <select className="input-field" value={staffId} onChange={e => setStaffId(e.target.value)}>
            <option value="">Select a worker…</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim()}</option>)}
          </select>
        </div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">From</label><input type="date" className="input-field" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">To</label><input type="date" className="input-field" value={to} onChange={e => setTo(e.target.value)} /></div>
        <button onClick={exportCsv} disabled={!rows.length} className="btn-secondary !py-2 flex items-center gap-1.5 disabled:opacity-40"><Download size={14} /> Export CSV</button>
      </div>

      {/* List */}
      {!staffId ? (
        <div className="card p-12 text-center text-slate-400"><MapPin size={26} className="mx-auto mb-2 text-slate-300" />Pick a worker to see their saved movement history.</div>
      ) : loading ? (
        <div className="card p-12 flex justify-center"><Loader2 className="animate-spin text-slate-300" size={26} /></div>
      ) : rows.length === 0 ? (
        <div className="card p-12 text-center text-slate-400"><MapPin size={26} className="mx-auto mb-2 text-slate-300" />No tracked shifts in this range. (Data appears once the worker tracks a shift with the latest app.)</div>
      ) : (
        <div className="card divide-y divide-slate-100">
          {rows.map(r => r.no_route ? (
            // Clocked in, but no GPS route reached the server for this shift.
            <div key={r.shift_id} className="w-full flex items-center gap-4 px-4 py-3.5">
              <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
                <MapPin size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{fmtDate(r.shift_date)} {r.client_name ? `· ${r.client_name}` : ''}</p>
                <p className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                  <span className="flex items-center gap-1"><Clock size={11} />Clocked in {fmt12(r.clock_in_hm)}{r.clock_out_hm ? ` · out ${fmt12(r.clock_out_hm)}` : ''}</span>
                </p>
                <p className="text-[11px] text-amber-700 mt-1">
                  No route recorded — the phone sent no GPS points for this shift. Routes are saved while the app is open on the shift, or all the time on Android with location set to “Allow all the time”.
                </p>
                {r.shift_address && <p className="text-[11px] text-slate-400 truncate mt-0.5">{r.shift_address}</p>}
              </div>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">No route</span>
            </div>
          ) : (
            <button key={r.shift_id} onClick={() => setOpen({ id: r.shift_id, name: workerName })}
              className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-slate-50 text-left transition-colors">
              <div className="w-11 h-11 rounded-xl bg-primary-50 text-primary-600 flex flex-col items-center justify-center shrink-0">
                <MapPin size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{fmtDate(r.started)} {r.client_name ? `· ${r.client_name}` : ''}</p>
                <p className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                  <span className="flex items-center gap-1"><Clock size={11} />{fmtTime(r.started)}–{fmtTime(r.ended)}</span>
                  <span>· {fmtDur(r.minutes)} tracked</span>
                  <span>· {r.points} points</span>
                </p>
                {(r.client_minutes != null || r.raw_km != null) && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {r.client_minutes != null && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Client {fmtDur(r.client_minutes)}</span>}
                    {r.community_minutes != null && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Community {fmtDur(r.community_minutes)}</span>}
                    {r.raw_km != null && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{r.raw_km} km</span>}
                    {r.stops != null && <span className="text-[10px] text-slate-400">{r.stops} stops</span>}
                  </div>
                )}
                {r.shift_address && <p className="text-[11px] text-slate-400 truncate mt-0.5">{r.shift_address}</p>}
              </div>
              <ChevronRight size={16} className="text-slate-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {open && <ShiftActivityPanel shiftId={open.id} title={open.name} onClose={() => setOpen(null)} />}
    </div>
  );
}
